import React, { useState } from "react";
import BgImage from "../assets/background/login_background.svg";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { Button, Form, Alert } from "react-bootstrap";
import { useMutation } from "@apollo/client";
import { USER_LOGIN_QUERY } from "../services/query";
import { INITIAL_VALUE, ERROR_MESSAGES } from "../constants/logIn";
import LeadLogo from "../assets/lead_logo.svg";
import UserService from "../utils/userService";
import { EVENT_NAMES_CONSTANT, logFirebaseEvent } from "../services/events";
import jwt_decode from "jwt-decode";
import { LOG_IN_TYPE } from "../constants/auth";

const LogInPage = () => {
  const [logIndata, setLogInData] = useState(INITIAL_VALUE);
  const [showPassword, setShowPassword] = useState(false);
  const [showAlert, setShowAlert] = useState();

  const {
    mobile: mobileErr,
    password: passwordErr,
    logIn: logInErr,
  } = ERROR_MESSAGES;

  const [userLogin] = useMutation(USER_LOGIN_QUERY);
  const onSubmit = async (e) => {
    e.preventDefault();

    if (logIndata.mobile.value === "" || logIndata.mobile.value.length < 10) {
      setLogInData((prevState) => ({
        ...prevState,
        mobile: {
          value: prevState.mobile.value,
          error: mobileErr.required,
        },
      }));
    }

    if (logIndata.password.value === "") {
      setLogInData((prevState) => ({
        ...prevState,
        password: {
          value: prevState.password.value,
          error: passwordErr.required,
        },
      }));

      return;
    }

    // event triggered when login btn clicked
    logInBtnClickEvent(LOG_IN_TYPE.nucleus, logIndata.mobile.value);

    try {
      const { data } = await userLogin({
        variables: {
          input: {
            mobile: logIndata.mobile.value || "",
            password: logIndata.password.value || "",
          },
        },
      });

      if (data?.authenticate && data?.authenticate?.success) {
        const response = UserService.handleSuccessfullLogin(data.authenticate);
        if (response?.error) {
          setShowAlert(response?.msg);
          // login tracking event
          logInFailedEvent(
            LOG_IN_TYPE.nucleus,
            logIndata.mobile.value,
            response?.msg
          );
        }
      } else if (!data?.authenticate?.success) {
        setShowAlert(data?.authenticate?.msg || logInErr?.default);
        // login tracking event
        logInFailedEvent(
          LOG_IN_TYPE.nucleus,
          logIndata.mobile.value,
          data?.authenticate?.msg || logInErr?.default
        );
      }
    } catch (error) {
      if (error?.networkError && error?.networkError.result) {
        setShowAlert(error?.networkError?.result?.msg || logInErr?.default);
        // login tracking event
        logInFailedEvent(
          LOG_IN_TYPE.nucleus,
          logIndata.mobile.value,
          error?.networkError?.result?.msg || logInErr?.default
        );
      } else {
        setShowAlert(logInErr.unknown);
        // login tracking event
        logInFailedEvent(
          LOG_IN_TYPE.nucleus,
          logIndata.mobile.value,
          logInErr.unknown
        );
      }
    }
  };

  const inputValidator = (text, fieldName) => {
    if (fieldName === "mobile") {
      // if number doesn't starts from 0-6 empty text box
      // gets triggred each time user input something
      if (!/^[6-9]/.test(text)) {
        setLogInData((prevState) => ({
          ...prevState,
          mobile: {
            value: text.slice(0, 0),
            error: mobileErr.invalid,
          },
        }));
        return;
      }
    }

    setLogInData((prevState) => ({
      ...prevState,
      [fieldName]: {
        value: text,
        error: "",
      },
    }));
  };

  const handleOnSuccess = async (response) => {
    // event triggered when email login btn clicked
    logInBtnClickEvent(
      LOG_IN_TYPE.google,
      jwt_decode(response.credential)?.email
    );
    await UserService.handleSuccessfullLogin(response, "google");
    document.getElementById("google-sign-in").hidden = true;
  };

  const handleOnFailure = (error) => {
    setShowAlert(error?.message || logInErr.google);
    logInFailedEvent(LOG_IN_TYPE.google, "", error?.message || logInErr.google);
  };

  const showAlertComponent = () => {
    if (showAlert) {
      return (
        <Alert variant="danger" className="error-alert-container">
          <div>
            <strong>{showAlert}</strong>
          </div>
        </Alert>
      );
    }
    return "";
  };

  const hideGoogleLogIn = () => {
    document.getElementById("google-sign-in").hidden = true;
    document.getElementById("text-hide").hidden = true;
  };

  const getLogProperties = (loginType, userId) => {
    let properties = {};
    if (loginType === "google") {
      properties = {
        email_id: userId,
      };
    } else {
      properties = {
        mobile_number: userId,
      };
    }

    return properties;
  };

  const logInBtnClickEvent = (loginType, userId) => {
    logFirebaseEvent(EVENT_NAMES_CONSTANT.login_clicked, {
      login_type: loginType,
      ...getLogProperties(loginType, userId),
    });
  };

  const logInFailedEvent = (loginType, userId, errorMsg) => {
    logFirebaseEvent(EVENT_NAMES_CONSTANT.login_failed, {
      login_type: loginType,
      error_message: errorMsg,
      ...getLogProperties(loginType, userId),
    });
  };

  return (
    <div className="auth-container">
      <GoogleOAuthProvider
        onScriptLoadError={hideGoogleLogIn}
        clientId="802591073379-9vnl80eou514mi6sskmfuqa2g4pucqhh.apps.googleusercontent.com"
      >
        <div className="image-section">
          <img className="bg-image" src={BgImage} alt="background" />
          <img className="login-page-logo" src={LeadLogo} alt="lead-logo"></img>
        </div>
        <div className="login-section">
          <div className="kpi-value">Greenfield</div>
          <div className="login-version-container center-content">
            Version: <b> {process.env.REACT_APP_VERSION}</b>
          </div>
          <div className="login-form-container">
            <div>
              {showAlertComponent()}
              <div className={`title ${showAlert ? "" : "title-margin-top"}`}>
                Login
              </div>
              <Form onSubmit={onSubmit}>
                <Form.Group className="mb-3">
                  <Form.Control
                    onChange={(e) => inputValidator(e.target.value, "mobile")}
                    className="input-box"
                    value={logIndata.mobile.value}
                    maxLength={10}
                    id="inlineFormInput"
                    placeholder="Enter mobile number"
                  />
                  <Form.Text className="error-text">
                    {logIndata.mobile.error}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3 password-form-group">
                  <Form.Control
                    onChange={(e) => inputValidator(e.target.value, "password")}
                    className="input-box"
                    id="inlineFormInput"
                    value={logIndata.password.value}
                    placeholder="Enter password"
                    type={showPassword ? "text" : "password"}
                  />
                  <div
                    className="password-icon"
                    role="presentation"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <i className="fa fa-eye" aria-hidden="true"></i>
                    ) : (
                      <i className="fa fa-eye-slash" aria-hidden="true"></i>
                    )}
                  </div>

                  <Form.Text className="error-text">
                    {logIndata.password.error}
                  </Form.Text>
                </Form.Group>
                <Button variant="primary" type="submit">
                  Submit
                </Button>
              </Form>
            </div>
            <div className="text" id="text-hide">
              Or,
            </div>
            <div id="google-sign-in" className="google-login-container">
              <GoogleLogin
                onSuccess={handleOnSuccess}
                onError={handleOnFailure}
                useOneTap
              />
            </div>
          </div>
        </div>
      </GoogleOAuthProvider>
    </div>
  );
};

export default LogInPage;
